import { useCallback, useMemo, useState, useContext } from "react";
import { AppState } from "../../../redux/store";
import { Colors } from "../../../utils/colors";
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { useLocation } from "react-router-dom";
import { NavigationId } from "../../../navigation/AppParamList";
import MorePanel from "../../../screens/Profile/CreatorPanels/MorePanel";
import { DarkTheme, useTheme } from "../../../hooks/useTheme";
import { useNavigate } from "../../../hooks/useNavigate";
import { MixpanelContext } from "../../../context/AnalyticsService";
import {
  abbreviateNumber,
  userPhoneVerificationStatus,
} from "@markit/common.utils";
import { getEventSlice } from "../../../redux/slices/eventSlice";
import { getUnreadConversationsQuery } from "../../../redux/slices/conversationSlice";
import { onSnapshot } from "../../../firebase";
import { VerticalDivider } from "../../Dividers/VerticalDivider";
import { EventType, OnboardingFlowType, ThemeType } from "@markit/common.types";
import OnboardingFlowDot from "../../Onboarding/OnboardingFlowDot";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import CreatorModeSidebarProfile from "./CreatorModeSidebarProfile";
import NoticeBannersWrapper from "../NoticeBannersWrapper";
import CreatorModeSidebarBanners from "./CreatorModeSidebarBanners";
import DropdownArrowIcon from "../../DropdownsAndTabs/DropdownArrowIcon";

export enum MenuItemLabel {
  HOME = "Home",
  TEXTING = "Texting",
  CONVERSATIONS = "Conversations",
  TRACKING_LINKS = "Tracking Links",
  AUTO_DM = "Auto DM",
  MONETIZE = "Monetize",
  PRODUCTS = "Products",
  MEMBERSHIPS = "Memberships",
  PAYOUTS = "Payouts",
  AUDIENCE = "Audience",
  CONTACTS = "Contacts",
  DATA_QUESTIONS = "Data Questions",
  LISTS = "Lists",
  IMPORT = "Import",
  SETTINGS = "Settings",
  MORE = "More",
  INTEGRATIONS = "Integrations",
  BILLING = "Billing",
  PLANS = "Plans",
  TERMSOFUSE = "Terms of Use",
  PRIVACYPOLICY = "Privacy Policy",
  VERIFICATION_STATUS = "Verification Status",
}

const SubMonetizeMenuItems = [
  MenuItemLabel.PRODUCTS,
  MenuItemLabel.MEMBERSHIPS,
  MenuItemLabel.PAYOUTS,
];

const SubAudienceMenuItems = [
  MenuItemLabel.CONTACTS,
  MenuItemLabel.DATA_QUESTIONS,
  MenuItemLabel.LISTS,
  MenuItemLabel.IMPORT,
];

export const homePathTab = (pathname: string) => {
  switch (pathname) {
    case "/home":
    case NavigationId.IMPORT:
      return MenuItemLabel.HOME;
    case NavigationId.HOME_PRODUCTS:
    case NavigationId.NEW_PRODUCT:
      return MenuItemLabel.PRODUCTS;
    case NavigationId.HOME_TRACKING_LINKS:
      return MenuItemLabel.TRACKING_LINKS;
    case NavigationId.HOME_AUTO_DM:
      return MenuItemLabel.AUTO_DM;
    case NavigationId.HOME_TEXTING:
      return MenuItemLabel.TEXTING;
    case NavigationId.HOME_CONVERSATIONS:
      return MenuItemLabel.CONVERSATIONS;
    case NavigationId.HOME_CONTACTS:
      return MenuItemLabel.CONTACTS;
    case NavigationId.HOME_DATA_QUESTIONS:
      return MenuItemLabel.DATA_QUESTIONS;
    case NavigationId.HOME_MEMBERSHIPS:
      return MenuItemLabel.MEMBERSHIPS;
    case NavigationId.HOME_LISTS:
      return MenuItemLabel.LISTS;
    case NavigationId.HOME_IMPORT:
      return MenuItemLabel.IMPORT;
    case NavigationId.HOME_INTEGRATIONS:
      return MenuItemLabel.INTEGRATIONS;
    case NavigationId.HOME_PAYOUTS:
      return MenuItemLabel.PAYOUTS;
    case NavigationId.HOME_SETTINGS:
      return MenuItemLabel.SETTINGS;
    case NavigationId.HOME_FINISH_ONBOARDING:
    case NavigationId.HOME_ACCOUNT_STATUS:
    case NavigationId.HOME_REFERRAL_PROGRAM:
      return "";
    default:
      return MenuItemLabel.HOME;
  }
};

interface MenuItem {
  label: MenuItemLabel;
  selectedIcon: string;
  subItems?: MenuItemLabel[];
}

type CreatorModeSidebarProps = {
  morePanelVisible: boolean;
  setMorePanelVisible: (morePanelVisible: boolean) => void;
  eventTheme?: ThemeType;
};

const CreatorModeSidebar = (props: CreatorModeSidebarProps) => {
  const { morePanelVisible, setMorePanelVisible, eventTheme } = props;
  const navigate = useNavigate();
  const mixpanel = useContext(MixpanelContext);
  const accountData = useSelector(
    (state: AppState) => state.account.accountData
  );
  const { events: eventList } = useSelector(getEventSlice);
  const { pathname } = useLocation();
  const [showMonetizeItems, setShowMonetizeItems] = useState(true);
  const [showAudienceItems, setShowAudienceItems] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [hoveredItemLabel, setHoveredItemLabel] =
    useState<MenuItemLabel | null>(null);
  const { theme } = useTheme(eventTheme);
  const isDarkTheme = useMemo(() => theme && theme === DarkTheme, [theme]);

  // get the number of unread conversation messages
  useAsyncOnMount(async () => {
    const unreadQuery = await getUnreadConversationsQuery(accountData.uid);
    const unsubscribe = onSnapshot(unreadQuery, (snapshot) => {
      setUnreadMessages(snapshot.size);
    });
    return () => unsubscribe();
  });

  const foundProduct = useMemo(() => {
    const eventId = pathname.split("/")[2];
    const foundEvent = eventList.find((event) => event.id === eventId);
    return foundEvent;
  }, [eventList, pathname]);

  const menuItems: MenuItem[] = useMemo(() => {
    const items = [
      {
        label: MenuItemLabel.HOME,
        selectedIcon: "mdi:home-variant",
      },
      {
        label: MenuItemLabel.TEXTING,
        selectedIcon: "ion:paper-plane",
      },
      {
        label: MenuItemLabel.CONVERSATIONS,
        selectedIcon: "ion:chatbubbles",
      },
      {
        label: MenuItemLabel.AUTO_DM,
        selectedIcon: "ion:flash",
      },
      {
        label: MenuItemLabel.MONETIZE,
        selectedIcon: "ion:wallet",
        subItems: showMonetizeItems ? SubMonetizeMenuItems : undefined,
      },
      {
        label: MenuItemLabel.AUDIENCE,
        selectedIcon: "ion:people",
        subItems: showAudienceItems ? SubAudienceMenuItems : undefined,
      },
      {
        label: MenuItemLabel.SETTINGS,
        selectedIcon: "ion:cog",
      },
      {
        label: MenuItemLabel.MORE,
        selectedIcon: "mdi:dots-horizontal",
      },
    ];

    return items;
  }, [showAudienceItems, showMonetizeItems]);

  const foundHomePathTab = useMemo(() => {
    // if pathname includes /new-product, return the products tab
    if (pathname.includes(NavigationId.NEW_PRODUCT)) {
      return MenuItemLabel.PRODUCTS;
    }
    // if pathname is the profile subscribe form, return the products tab (forms live there)
    if (pathname.includes(NavigationId.HOME_SUBSCRIBE_FORM)) {
      return MenuItemLabel.PRODUCTS;
    }
    // check if on dashboard if it's a generic link or event
    if (pathname.includes("/e/")) {
      if (foundProduct) {
        return foundProduct.eventType === EventType.GENERIC_LINK_NO_DATA
          ? MenuItemLabel.TRACKING_LINKS
          : MenuItemLabel.PRODUCTS;
      }
    }
    // check if on auto dm dashboard
    if (pathname.includes("/dm")) {
      return MenuItemLabel.AUTO_DM;
    }
    // check if on audience list dashboard
    if (pathname.includes("/a/")) {
      return MenuItemLabel.LISTS;
    }
    // check if on import page
    if (pathname.includes("/import")) {
      return MenuItemLabel.IMPORT;
    }
    const foundTab = homePathTab(pathname);
    return foundTab;
  }, [foundProduct, pathname]);

  const isSelectedMenuItem = useCallback(
    (item: MenuItemLabel) => {
      return (
        pathname !== NavigationId.HOME_PROFILE &&
        ((foundHomePathTab === item && !morePanelVisible) ||
          (morePanelVisible && item === MenuItemLabel.MORE) ||
          (item === MenuItemLabel.MORE &&
            (foundHomePathTab === MenuItemLabel.INTEGRATIONS ||
              foundHomePathTab === MenuItemLabel.TRACKING_LINKS)))
      );
    },
    [foundHomePathTab, morePanelVisible, pathname]
  );

  const menuItemOnPress = useCallback(
    (item: MenuItemLabel) => {
      if (item === MenuItemLabel.MORE) {
        setMorePanelVisible(!morePanelVisible);
        return;
      }

      switch (item) {
        case MenuItemLabel.HOME:
          navigate("/home");
          break;
        case MenuItemLabel.TEXTING:
          navigate(NavigationId.HOME_TEXTING);
          break;
        case MenuItemLabel.CONVERSATIONS:
          navigate(NavigationId.HOME_CONVERSATIONS);
          break;
        case MenuItemLabel.MONETIZE:
          setShowMonetizeItems(!showMonetizeItems);
          break;
        case MenuItemLabel.AUDIENCE:
          setShowAudienceItems(!showAudienceItems);
          break;
        case MenuItemLabel.SETTINGS:
          navigate(NavigationId.HOME_SETTINGS);
          break;
        case MenuItemLabel.CONTACTS:
          navigate(NavigationId.HOME_CONTACTS);
          break;
        case MenuItemLabel.DATA_QUESTIONS:
          navigate(NavigationId.HOME_DATA_QUESTIONS);
          break;
        case MenuItemLabel.MEMBERSHIPS:
          navigate(NavigationId.HOME_MEMBERSHIPS);
          break;
        case MenuItemLabel.LISTS:
          navigate(NavigationId.HOME_LISTS);
          break;
        case MenuItemLabel.IMPORT:
          navigate(NavigationId.HOME_IMPORT);
          break;
        case MenuItemLabel.PRODUCTS:
          navigate(NavigationId.HOME_PRODUCTS);
          break;
        case MenuItemLabel.TRACKING_LINKS:
          navigate(NavigationId.HOME_TRACKING_LINKS);
          break;
        case MenuItemLabel.AUTO_DM:
          navigate(NavigationId.HOME_AUTO_DM);
          break;
        case MenuItemLabel.PAYOUTS:
          navigate(NavigationId.HOME_PAYOUTS);
          break;
        case MenuItemLabel.VERIFICATION_STATUS:
          mixpanel.track("Pressed Verification Account Status Notice", {
            distinct_id: accountData.uid,
            verificationStatus: userPhoneVerificationStatus(accountData),
          });
          navigate(NavigationId.HOME_ACCOUNT_STATUS);
          break;
        default:
          navigate("/home");
      }
      setMorePanelVisible(false);
    },
    [
      accountData,
      mixpanel,
      morePanelVisible,
      navigate,
      setMorePanelVisible,
      showAudienceItems,
      showMonetizeItems,
    ]
  );

  const SidebarItem = useCallback(
    (label: MenuItemLabel, icon?: string) => (
      <m.div
        className="AlignedRowSpaced CreatorModeMenuItemContainer"
        onClick={() => menuItemOnPress(label)}
        style={{
          backgroundColor:
            isSelectedMenuItem(label) || hoveredItemLabel === label
              ? isDarkTheme
                ? Colors.GRAY7
                : Colors.GRAY6
              : isDarkTheme
                ? Colors.GRAY10
                : Colors.WHITE,
        }}
        onHoverStart={() => setHoveredItemLabel(label)}
        onHoverEnd={() => setHoveredItemLabel(null)}
        transition={{ duration: 0.1 }}
      >
        <div className="AlignedRowSelect" style={{ gap: 10 }}>
          {icon ? (
            <div style={{ cursor: "pointer", paddingTop: 1 }}>
              <Icon
                icon={icon}
                height={20}
                style={{ color: theme?.PrimaryText.color }}
              />
            </div>
          ) : (
            <div style={{ paddingInline: 10 }}>
              <VerticalDivider
                height={41}
                color={
                  isSelectedMenuItem(label)
                    ? theme.PrimaryText.color
                    : isDarkTheme
                      ? theme.DividerColor.borderColor
                      : undefined
                }
              />
            </div>
          )}
          <span className="bodyMedium" style={theme?.PrimaryText}>
            {label}
          </span>
          {label === MenuItemLabel.AUDIENCE ||
          label === MenuItemLabel.IMPORT ? (
            <OnboardingFlowDot
              flowTypes={[OnboardingFlowType.IMPORT_CONTACTS]}
            />
          ) : null}
          {label === MenuItemLabel.TEXTING ? (
            <OnboardingFlowDot
              flowTypes={[OnboardingFlowType.SEND_FIRST_TEXT]}
            />
          ) : null}
          {label === MenuItemLabel.MONETIZE ? (
            <OnboardingFlowDot
              flowTypes={[
                OnboardingFlowType.NEWSLETTER_BLOG,
                OnboardingFlowType.ORGANIZE_EVENTS,
                OnboardingFlowType.SELL_PRODUCTS,
                OnboardingFlowType.PAID_MEMBERSHIP,
                OnboardingFlowType.COLLECT_INFO,
              ]}
            />
          ) : label === MenuItemLabel.PRODUCTS ? (
            <OnboardingFlowDot
              flowTypes={[
                OnboardingFlowType.NEWSLETTER_BLOG,
                OnboardingFlowType.ORGANIZE_EVENTS,
                OnboardingFlowType.SELL_PRODUCTS,
                OnboardingFlowType.COLLECT_INFO,
              ]}
            />
          ) : label === MenuItemLabel.MEMBERSHIPS ? (
            <OnboardingFlowDot
              flowTypes={[OnboardingFlowType.PAID_MEMBERSHIP]}
            />
          ) : null}
          {label === MenuItemLabel.AUTO_DM ? (
            <OnboardingFlowDot flowTypes={[OnboardingFlowType.INSTAGRAM_DM]} />
          ) : null}
          {label === MenuItemLabel.SETTINGS ? (
            <OnboardingFlowDot flowTypes={[OnboardingFlowType.MCP_API]} />
          ) : null}
        </div>
        {label === MenuItemLabel.AUDIENCE ||
        label === MenuItemLabel.MONETIZE ? (
          <DropdownArrowIcon
            isOpen={
              (label === MenuItemLabel.AUDIENCE && showAudienceItems) ||
              (label === MenuItemLabel.MONETIZE && showMonetizeItems)
            }
            color={theme?.PrimaryText.color}
            height={18}
            style={{ marginBottom: -2 }}
          />
        ) : null}
        {label === MenuItemLabel.CONVERSATIONS && unreadMessages > 0 ? (
          <div
            className="Centering"
            style={{
              padding: 2,
              minWidth: 16,
              minHeight: 16,
              borderRadius: 100,
              backgroundColor: Colors.RED3,
            }}
          >
            <span
              style={{
                fontSize: unreadMessages > 99 ? 9 : 10,
                color: Colors.WHITE,
              }}
            >
              {abbreviateNumber(unreadMessages)}
            </span>
          </div>
        ) : null}
      </m.div>
    ),
    [
      hoveredItemLabel,
      isDarkTheme,
      isSelectedMenuItem,
      menuItemOnPress,
      showAudienceItems,
      showMonetizeItems,
      theme.DividerColor.borderColor,
      theme.PrimaryText,
      unreadMessages,
    ]
  );

  const SidebarMenuItems = useMemo(() => {
    return (
      <LazyMotion features={domAnimation}>
        <div className="ColumnNormal PreventScroll" style={{ width: 194 }}>
          <div className="ColumnNormal AllowScroll">
            <CreatorModeSidebarProfile eventTheme={eventTheme} />
            <div
              className="ColumnNormal"
              style={{ padding: "0px 10px 7px 10px", gap: 10 }}
            >
              <NoticeBannersWrapper
                onVerificationStatusPress={() =>
                  menuItemOnPress(MenuItemLabel.VERIFICATION_STATUS)
                }
                eventTheme={eventTheme}
              />
              <div className="ColumnNormal">
                {menuItems.map((item) => {
                  return (
                    <div className="ColumnNormal" key={item.label}>
                      {SidebarItem(item.label, item.selectedIcon)}
                      {item.subItems ? (
                        <div className="ColumnNormal">
                          {item.subItems.map((subItem) => SidebarItem(subItem))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {eventTheme === undefined ? <CreatorModeSidebarBanners /> : null}
        </div>
      </LazyMotion>
    );
  }, [eventTheme, menuItems, menuItemOnPress, SidebarItem]);

  return (
    <div
      className="CreatorModeLeftPanel AlignedRow"
      style={{
        backgroundColor: isDarkTheme ? Colors.GRAY10 : Colors.WHITE,
        borderRight: isDarkTheme
          ? "0.5px solid #3d3d3d"
          : `0.5px solid ${Colors.GRAY11}`,
      }}
    >
      {SidebarMenuItems}
      <MorePanel
        morePanelVisible={morePanelVisible}
        setMorePanelVisible={setMorePanelVisible}
        theme={theme}
      />
    </div>
  );
};

export default CreatorModeSidebar;
